import React, { useState, Fragment } from "react";
import { motion } from "framer-motion";
import { Dialog, Transition } from "@headlessui/react";
import { Phone, X, ChevronDown } from "lucide-react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import emailjs from "emailjs-com";

const EMAILJS_SERVICE_ID = "service_y4digmd";
const EMAILJS_USER_ID = "CldZ_Fp5B3Q4dDYut";

const CallToAction = () => {
  // Modal states
  const [open, setOpen] = useState(false); // first modal (role+inquiry)
  const [calendarOpen, setCalendarOpen] = useState(false); // second modal (calendar)
  const [confirmOpen, setConfirmOpen] = useState(false); // confirmation modal

  // Dropdowns
  const [role, setRole] = useState("");
  const [inquiry, setInquiry] = useState("");

  // Calendar / slots
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");

  // Confirmation form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  // UI states
  const [sending, setSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);
  const [sendError, setSendError] = useState("");

  const roleOptions = [
    "Founder",
    "HR / Company representative",
    "Other (not related to job offer or freelance work)",
  ];
  const inquiryOptions = [
    "Want to hire you for job / job offer",
    "Have some freelance work",
    "None of the above",
  ];

  const resetAll = () => {
    setRole("");
    setInquiry("");
    setSelectedDate(null);
    setSelectedTime("");
    setName("");
    setEmail("");
    setNotes("");
    setSending(false);
    setSentSuccess(false);
    setSendError("");
  };

  // Example time slots — you can make this dynamic per date
  const timeSlots = [
    "09:00 AM",
    "10:30 AM",
    "12:00 PM",
    "02:30 PM",
    "04:30 PM",
    "05:00 PM",
  ];

  // When user clicks Continue on the first modal: open calendar modal
  const handleContinueToCalendar = () => {
    if (!role || !inquiry) return;
    setOpen(false);
    // reset calendar/time state
    setSelectedDate(null);
    setSelectedTime("");
    setCalendarOpen(true);
  };

  // When user confirms date/time in calendar modal, open confirmation modal
  const handleConfirmMeeting = () => {
    if (!selectedDate || !selectedTime) return;
    setCalendarOpen(false);
    setConfirmOpen(true);
    // Optionally prefill name/email if you have them saved in app
  };

  // Convert selectedDate -> formatted string
  const formatDate = (d) => {
    if (!d) return "";
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSendBooking = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      setSendError("Please provide your name and email.");
      return;
    }

    setSending(true);
    setSendError("");
    setSentSuccess(false);

    // Template params for both emails
    const templateParams = {
      name,
      email, // user's email
      notes,
      role,
      inquiry,
      date: formatDate(selectedDate),
      time: selectedTime,
    };

    try {
      // 1️⃣ Send email TO YOU (designer)
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        "template_g45oceb", 
        {
          ...templateParams,
          to_email: "ashishkarche9@gmail.com", // <-- your email here
          reply_to: email, // user email for easy reply
        },
        EMAILJS_USER_ID
      );

      // 2️⃣ Send confirmation email TO USER
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        "template_ghqm3er", // EmailJS template for user confirmation
        {
          ...templateParams,
          to_email: email, // send to the user's email
        },
        EMAILJS_USER_ID
      );

      setSentSuccess(true);
      setSending(false);
    } catch (err) {
      setSendError("Something went wrong while sending. Try again later.");
      setSending(false);
      console.error("EmailJS error:", err);
    }
  };

  return (
    <>
      {/* ================= CTA SECTION ================= */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
        className="mt-28 w-full flex justify-center px-4"
      >
        <div
          className="w-full max-w-5xl rounded-2xl border border-dashed border-gray-400/40
          dark:border-gray-600/40 bg-gray-100/10 dark:bg-gray-800/20 py-14 text-center backdrop-blur-sm"
        >
          <h2 className="text-lg sm:text-xl font-medium text-gray-800 dark:text-gray-200 mb-6">
            Hey, you scrolled this far, let's talk.
          </h2>

          <motion.button
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.04 }}
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border
            border-gray-400/40 dark:border-gray-600/40 bg-gray-50 dark:bg-gray-900
            text-gray-900 dark:text-white text-sm font-medium shadow-[0_0_10px_rgba(0,0,0,0.15)]
            hover:shadow-[0_0_14px_rgba(0,0,0,0.25)] transition-shadow"
          >
            <img
              src="/avatar.png"
              alt="avatar"
              className="w-5 h-5 rounded-full object-cover"
            />
            Book a Free Call
            <Phone size={16} className="ml-1" />
          </motion.button>
        </div>
      </motion.section>

      {/* ================= FIRST MODAL: role + inquiry ================= */}
      <Transition appear show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setOpen(false);
            resetAll();
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center px-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-xl border border-gray-200/30 dark:border-gray-700/40">
                <div className="flex justify-between items-center mb-2">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                    Tell me about yourself
                  </Dialog.Title>
                  <button onClick={() => setOpen(false)}>
                    <X className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Please select your inquiry type to continue.
                </p>

                <div className="space-y-4">
                  {/* Role */}
                  <div>
                    <label className="text-sm text-gray-700 dark:text-gray-300 block mb-1">
                      Are you a?
                    </label>
                    <div className="relative">
                      <select
                        value={role}
                        onChange={(e) => {
                          setRole(e.target.value);
                          setInquiry(""); // reset second if role changes
                        }}
                        className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-800
                          text-gray-900 dark:text-white appearance-none outline-none cursor-pointer"
                      >
                        <option value="">Select an option...</option>
                        {roleOptions.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        className="absolute right-3 top-3.5 text-gray-500"
                        size={16}
                      />
                    </div>
                  </div>

                  {/* Inquiry */}
                  <div>
                    <label className="text-sm text-gray-700 dark:text-gray-300 block mb-1">
                      What's your inquiry about?
                    </label>
                    <div className="relative">
                      <select
                        disabled={!role}
                        value={inquiry}
                        onChange={(e) => setInquiry(e.target.value)}
                        className={`w-full px-4 py-3 rounded-lg appearance-none outline-none ${
                          !role
                            ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white cursor-pointer"
                        }`}
                      >
                        <option value="">Select an option...</option>
                        {inquiryOptions.map((q) => (
                          <option key={q} value={q}>
                            {q}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        className="absolute right-3 top-3.5 text-gray-500"
                        size={16}
                      />
                    </div>
                  </div>

                  <div>
                    <button
                      onClick={handleContinueToCalendar}
                      disabled={!role || !inquiry}
                      className={`w-full py-3 rounded-md font-medium transition ${
                        !role || !inquiry
                          ? "bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-95"
                      }`}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ================= SECOND MODAL: Calendar + Time Slots ================= */}
      <Transition appear show={calendarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setCalendarOpen(false);
            resetAll();
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/65 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center px-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-xl border border-gray-200/30 dark:border-gray-700/40">
                <div className="flex justify-between items-start">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                      Book a Meeting
                    </Dialog.Title>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Pick a date and a time slot that works for you.
                    </p>
                  </div>
                  <button onClick={() => setCalendarOpen(false)}>
                    <X className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" />
                  </button>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Left: info */}
                  <div className="md:col-span-1 p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Meeting
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                      Hey, let's have a chat
                    </p>

                    <ul className="text-sm mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                      <li>• Requires confirmation</li>
                      <li>• Duration: 30m</li>
                      <li>• Platform: Google Meet</li>
                      <li>• Timezone: Your local timezone</li>
                    </ul>
                  </div>

                  {/* Middle: calendar */}
                  <div className="md:col-span-1 p-4 rounded-lg bg-white dark:bg-gray-800">
                    <Calendar
                      onChange={(d) => {
                        // react-calendar returns Date or array — we only allow single date
                        setSelectedDate(d);
                        setSelectedTime("");
                      }}
                      value={selectedDate}
                      minDate={new Date()}
                      className="rounded-md"
                    />
                  </div>

                  {/* Right: time slots */}
                  <div className="md:col-span-1 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 flex flex-col">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Time slots
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                      Select a time after picking a date.
                    </p>

                    <div className="mt-4 space-y-3 overflow-auto max-h-64">
                      {!selectedDate ? (
                        <div className="text-sm text-gray-500">
                          Please pick a date to see slots.
                        </div>
                      ) : (
                        timeSlots.map((t) => (
                          <button
                            key={t}
                            onClick={() => setSelectedTime(t)}
                            className={`w-full text-left px-3 py-2 rounded-md border ${
                              selectedTime === t
                                ? "border-blue-600 bg-blue-600/10 text-blue-700 dark:text-blue-200"
                                : "border-gray-200 dark:border-gray-700 bg-transparent text-gray-800 dark:text-gray-200"
                            }`}
                          >
                            {t}
                          </button>
                        ))
                      )}
                    </div>

                    <div className="mt-auto">
                      <button
                        disabled={!selectedDate || !selectedTime}
                        onClick={handleConfirmMeeting}
                        className={`mt-6 w-full py-3 rounded-md font-medium transition ${
                          !selectedDate || !selectedTime
                            ? "bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                            : "bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-95"
                        }`}
                      >
                        Confirm Meeting
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ================= THIRD MODAL: Confirmation form & send ================= */}
      <Transition appear show={confirmOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setConfirmOpen(false);
            resetAll();
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/65 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center px-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-xl border border-gray-200/30 dark:border-gray-700/40">
                <div className="flex justify-between items-center mb-2">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                    Confirm your meeting
                  </Dialog.Title>
                  <button onClick={() => setConfirmOpen(false)}>
                    <X className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {formatDate(selectedDate)} · {selectedTime}
                </p>

                <form onSubmit={handleSendBooking} className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-700 dark:text-gray-300 block mb-1">
                      Your name
                    </label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white outline-none"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-gray-700 dark:text-gray-300 block mb-1">
                      Email
                    </label>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      type="email"
                      className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white outline-none"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-gray-700 dark:text-gray-300 block mb-1">
                      A short note (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white outline-none resize-none"
                      placeholder="Add anything you'd like me to know before the call"
                    />
                  </div>

                  {sendError && (
                    <div className="text-sm text-red-500">{sendError}</div>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={sending || sentSuccess}
                      className={`w-full py-3 rounded-md font-medium transition ${
                        sending || sentSuccess
                          ? "bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:opacity-95"
                      }`}
                    >
                      {sending
                        ? "Booking..."
                        : sentSuccess
                        ? "Booked ✓"
                        : "Book Now"}
                    </button>
                  </div>

                  {sentSuccess && (
                    <div className="mt-3 text-sm text-green-500">
                      Booking confirmed — check your email for details.
                    </div>
                  )}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CallToAction;
